import { EDecision } from '@if/core/types'
import * as yup from '@if/core/yup'
import { ActionButton, type FormDialogContentContext, useFormDialog } from '@if/mui'
import { Box, CircularProgress, Divider, Grid, List, ListItemButton, ListItemText, TextField } from '@mui/material'
import { reportErrorToConsole } from '@src/services/error-logger'
import { ApplicantDto } from '@src/types/CreditApplicationSchema'
import axios, { AxiosRequestConfig } from 'axios'
import { every } from 'lodash-es'
import { useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

interface TestCase {
  id: string
  applicantDecisionAuto: EDecision
  applicant: ApplicantDto
  description: string
}

/** The instance form: just the picked applicant, `required` so Confirm (Load) gates on a selection. */
interface TestCaseForm {
  selectedApplicant: ApplicantDto | null
}

const testCaseSchema = yup.object({
  selectedApplicant: yup.mixed<ApplicantDto>().required(),
})

function loadTestCases(): Promise<TestCase[]> {
  const payload: AxiosRequestConfig = {
    method: 'GET',
    url: 'https://func-if-proxies-dev-cac.azurewebsites.net/CreditTestCases',
  }
  return axios.request(payload).then((response) => {
    const res = response.data as TestCase[]
    res.forEach((x: TestCase, index) => {
      x.id = index.toString()
    })
    return res
  })
}

function getCase(cases: TestCase[], decision: EDecision): ApplicantDto | null {
  const matching = cases.filter((c) => c.applicantDecisionAuto === decision)
  return matching.length > 0 ? matching[0].applicant : null
}

interface TestCaseItemProps {
  onTestCaseSelected: (applicant: ApplicantDto) => void
  testcase: TestCase
  selected: boolean
}

const TestCaseItem = ({ onTestCaseSelected, testcase, selected }: TestCaseItemProps) => {
  const { applicant } = testcase
  const primaryText = `${applicant.firstName} ${applicant.lastName} (${applicant.birthDate?.toString() ?? ''})`
  return (
    <>
      <ListItemButton onClick={() => onTestCaseSelected(testcase.applicant)} selected={selected}>
        <ListItemText primary={primaryText} secondary={testcase.description} />
      </ListItemButton>
      <Divider />
    </>
  )
}

/** Dialog body: quick-decision shortcuts (resolve immediately) + a filterable list whose pick drives the form. */
const TestCaseDialogContent = ({ ctx }: { ctx: FormDialogContentContext<TestCaseForm, void, ApplicantDto> }) => {
  const { t } = useTranslation()
  const [testcases, setTestCases] = useState<TestCase[]>([])
  const [textFilter, setTextFilter] = useState('')
  const [isLoading, setLoading] = useState(true)

  useEffect(() => {
    loadTestCases()
      .then((values) => setTestCases(values))
      .catch(reportErrorToConsole)
      .finally(() => setLoading(false))
  }, [])

  const filteredTestCases = useMemo(() => {
    const filters = textFilter.toLowerCase().split(' ')
    return testcases.filter((testcase) =>
      every(
        filters,
        (f) =>
          testcase.applicant.firstName.toLowerCase().includes(f) ||
          (testcase.applicant.lastName ?? '').toLowerCase().includes(f) ||
          testcase.description.toLowerCase().includes(f),
      ),
    )
  }, [textFilter, testcases])

  const approvedCase = useMemo(() => getCase(testcases, EDecision.Approved), [testcases])
  const reviewCase = useMemo(() => getCase(testcases, EDecision.ManualRevision), [testcases])
  const declineCase = useMemo(() => getCase(testcases, EDecision.Refused), [testcases])

  const selectedApplicant = ctx.form.watch('selectedApplicant')

  return (
    <>
      <Grid container spacing={2} padding={2}>
        <Grid>
          <TextField
            name="filter"
            onChange={(e) => setTextFilter(e.target.value)}
            value={textFilter}
            label={t('common.filter')}
          />
        </Grid>
        <Grid flexGrow={1} />
        <Grid
          container
          direction={{ xs: 'column', sm: 'row' }}
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={1}
          size={{ xs: 12, sm: 'auto' }}
        >
          {approvedCase && (
            <Grid>
              <ActionButton emphasis="principal" tone="success" onClick={() => ctx.close(approvedCase)}>
                {t('common.testCaseApproved')}
              </ActionButton>
            </Grid>
          )}
          {reviewCase && (
            <Grid>
              <ActionButton emphasis="principal" tone="warning" onClick={() => ctx.close(reviewCase)}>
                {t('common.testCaseReview')}
              </ActionButton>
            </Grid>
          )}
          {declineCase && (
            <Grid>
              <ActionButton emphasis="principal" tone="destructive" onClick={() => ctx.close(declineCase)}>
                {t('common.testCaseDeclined')}
              </ActionButton>
            </Grid>
          )}
        </Grid>
      </Grid>
      <Divider />
      <Box height={400} width="100%">
        {isLoading && <CircularProgress />}
        {!isLoading && filteredTestCases.length > 0 && (
          <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
            {filteredTestCases.map((testcase) => (
              <TestCaseItem
                key={testcase.id}
                testcase={testcase}
                onTestCaseSelected={(applicant) =>
                  ctx.form.setValue('selectedApplicant', applicant, { shouldValidate: true, shouldDirty: true })
                }
                selected={testcase.applicant === selectedApplicant}
              />
            ))}
          </List>
        )}
        {!isLoading && filteredTestCases.length === 0 && <p>{t('common.noTestCaseMatchesFilter')}</p>}
      </Box>
    </>
  )
}

/** Dev-only "load a test case" picker, wired as a `useFormDialog`; resolves the chosen applicant. */
export const useTestCaseDialog = () => {
  const { t } = useTranslation()
  return useFormDialog<TestCaseForm, void, ApplicantDto>({
    title: t('common.selectTestCase'),
    confirmLabel: t('common.load'),
    schema: testCaseSchema,
    defaultValues: { selectedApplicant: null },
    content: (ctx) => <TestCaseDialogContent ctx={ctx} />,
    onSubmit: (data) => data.selectedApplicant!,
    successMessage: false,
  })
}
